import {
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  LineDashedMaterial,
  LineLoop,
  Mesh,
  MeshLambertMaterial,
  type Material,
} from 'three';
import { EN22 } from '../../core/enemies/antenna';

/**
 * EN-22 Antena del "KGB de Cali" (GDD §8.3, issue #105): a low-poly dish on a mast and a dashed ring that marks
 * its surveillance zone of 3 u, in the cool accent of the world; it turns amber once triggered, never the red of
 * the alerts. The antenna has no hitbox of its own.
 */
export interface AntennaModel {
  group: Group;
  setTriggered(triggered: boolean): void;
  dispose(): void;
}

export const ANTENNA_COLORS = { mast: 0x5f6670, dish: 0xc9ced6, zone: 0x4bb6c9, triggered: 0xe0a030 } as const;

export function buildAntenna(name: string): AntennaModel {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const group = new Group();
  group.name = name;
  const mastGeometry = new CylinderGeometry(0.05, 0.08, 0.9, 5);
  const dishGeometry = new ConeGeometry(0.35, 0.18, 8, 1, true);
  geometries.push(mastGeometry, dishGeometry);
  const mast = new Mesh(mastGeometry, paint(ANTENNA_COLORS.mast));
  mast.position.y = -0.45;
  const dish = new Mesh(dishGeometry, paint(ANTENNA_COLORS.dish));
  dish.rotation.z = Math.PI / 4;
  dish.position.y = 0.05;
  group.add(mast, dish);

  // The zone: a dashed circle of the zone radius around the tip.
  const points: number[] = [];
  for (let i = 0; i < 48; i++) {
    const angle = (i / 48) * Math.PI * 2;
    points.push(Math.cos(angle) * EN22.zoneRadius, Math.sin(angle) * EN22.zoneRadius, 0);
  }
  const ringGeometry = new BufferGeometry();
  ringGeometry.setAttribute('position', new Float32BufferAttribute(points, 3));
  geometries.push(ringGeometry);
  const ringMaterial = new LineDashedMaterial({ color: ANTENNA_COLORS.zone, dashSize: 0.35, gapSize: 0.25 });
  materials.push(ringMaterial);
  const ring = new LineLoop(ringGeometry, ringMaterial);
  ring.computeLineDistances();
  ring.name = `${name}.zone`;
  group.add(ring);

  return {
    group,
    setTriggered(triggered) {
      ringMaterial.color.setHex(triggered ? ANTENNA_COLORS.triggered : ANTENNA_COLORS.zone);
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
